import React from 'react';
import type { ProductListOptionFilter } from 'graphql-ts-types';

import FilterSection from 'common/components/categories/FilterSection';
import FilterSectionTitle, {
  FilterSectionTitleKind,
} from 'common/components/categories/FilterSectionTitle';
import { useFilterHandlers } from 'common/components/categories/hooks/useFilterHandlers';
import { useFilterSearch } from 'common/components/categories/hooks/useFilterSearch';
import { Flex } from 'common/components/Flex';
import { useAccordion } from 'common/hooks/accordion';
import { useLocalization } from 'common/providers/LocaleProvider';
import { trackToggleFilter } from 'common/tracking/filters';
import type { Filter } from 'common/types/SortAndFilter';
import { getGroupAppliedValues } from 'common/utils/filterGroups';
import type { FilterChangeParams } from '../context/FilterDrawerContext';
import FilterAccordionSection from './FilterAccordionSection';

interface FilterAccordionProps {
  filters: Filter[];
  onFilterChange: (params: FilterChangeParams) => void;
  defaultExpandedSections: string[];
  hideArticlesCount: boolean;
  maxItemsVisible: number;
  groupName?: string;
  groupKey?: string;
}

const FILTER_SEARCH_THRESHOLD = 15;

const filterEmpty = (filters: Filter[]): Filter[] =>
  filters.filter(filter =>
    'options' in filter ? filter.options && filter.options.length : filter.min !== filter.max
  );

interface FilterRangeParams {
  newMin: number;
  newMax: number;
  min: number;
  max: number;
}

interface BaseFilterSectionProps {
  searchValues: Record<string, string>;
  filteredOptions: Record<string, ProductListOptionFilter['options']>;
  onSearchValueChange: (filterKey: string, value: string) => void;
  onFilterOptionChange: (filterKey: string, optionsKeys: string[]) => void;
  onFilterRangeChange: (filterKey: string, range: FilterRangeParams) => void;
  maxItemsVisible: number;
  rangesWithApplyButton: boolean;
  hideArticlesCount: boolean;
  isDrawer: boolean;
  showSelectAllOption: boolean;
  searchThreshold: number;
}

interface GroupedSectionProps {
  groupKey: string;
  groupName: string;
  filters: Filter[];
  isExpanded: (key: string) => boolean;
  onToggle: (key: string) => void;
  baseFilterSectionProps: BaseFilterSectionProps;
}

const GroupedSection = ({
  groupKey,
  groupName,
  filters,
  isExpanded,
  onToggle,
  baseFilterSectionProps,
}: GroupedSectionProps) => {
  const { r } = useLocalization();
  const appliedValues = getGroupAppliedValues(filters);

  return (
    <FilterAccordionSection
      key={groupKey}
      sectionKey={groupKey}
      title={
        <FilterSectionTitle
          kind={FilterSectionTitleKind.Grouped}
          name={groupName}
          appliedValues={appliedValues}
        />
      }
      isExpanded={isExpanded(groupKey)}
      onToggle={() => onToggle(groupKey)}
    >
      {filters.map(({ key, ...filter }) => (
        <FilterSection
          key={key}
          {...baseFilterSectionProps}
          filter={{ ...filter, key } as Filter}
          filterKey={key}
          placeholder={r.t('h24_filter_search_label', { name: filter.name })}
        />
      ))}
    </FilterAccordionSection>
  );
};

interface IndividualSectionsProps {
  filters: Filter[];
  isExpanded: (key: string) => boolean;
  onToggle: (key: string) => void;
  baseFilterSectionProps: BaseFilterSectionProps;
}

const IndividualSections = ({
  filters,
  isExpanded,
  onToggle,
  baseFilterSectionProps,
}: IndividualSectionsProps) => {
  const { r } = useLocalization();

  return (
    <>
      {filters.map(({ key, ...filter }) => (
        <FilterAccordionSection
          key={key}
          sectionKey={key}
          title={
            <FilterSectionTitle
              kind={
                'options' in filter ? FilterSectionTitleKind.Option : FilterSectionTitleKind.Range
              }
              {...filter}
            />
          }
          isExpanded={isExpanded(key)}
          onToggle={() => onToggle(key)}
        >
          <Flex flexDirection="column" gap="sp_8" px="sp_8" pb="sp_8">
            <FilterSection
              {...baseFilterSectionProps}
              filter={{ ...filter, key } as Filter}
              filterKey={key}
              placeholder={r.t('h24_filter_search_label', { name: filter.name })}
            />
          </Flex>
        </FilterAccordionSection>
      ))}
    </>
  );
};

const FilterAccordion = ({
  filters: initialFilters = [],
  onFilterChange,
  defaultExpandedSections,
  hideArticlesCount,
  maxItemsVisible,
  groupName,
  groupKey,
}: FilterAccordionProps) => {
  const filters = filterEmpty(initialFilters);
  const [toggle, isExpanded] = useAccordion(true, defaultExpandedSections);
  const { searchValues, filteredOptions, onSearchValueChange } = useFilterSearch(filters);
  const { onFilterOptionChange, onFilterRangeChange } = useFilterHandlers(onFilterChange);

  const trackToggle = (sectionName: string) => {
    trackToggleFilter({
      filter: sectionName,
      isExpanded: isExpanded(sectionName),
      isDrawer: true,
    });
    toggle(sectionName);
  };

  const baseFilterSectionProps = {
    searchValues,
    filteredOptions,
    onSearchValueChange,
    onFilterOptionChange,
    onFilterRangeChange,
    maxItemsVisible,
    rangesWithApplyButton: false,
    hideArticlesCount,
    isDrawer: true,
    showSelectAllOption: true,
    searchThreshold: FILTER_SEARCH_THRESHOLD,
  };

  if (groupKey && groupName) {
    return (
      <GroupedSection
        groupKey={groupKey}
        groupName={groupName}
        filters={filters}
        isExpanded={isExpanded}
        onToggle={trackToggle}
        baseFilterSectionProps={baseFilterSectionProps}
      />
    );
  }

  return (
    <IndividualSections
      filters={filters}
      isExpanded={isExpanded}
      onToggle={trackToggle}
      baseFilterSectionProps={baseFilterSectionProps}
    />
  );
};

export default FilterAccordion;
